<template>
  <div :class="containerClass">
    <column class="d-flex">
      <slot name="value" v-if="!formulaText" :fieldName="fieldName" :model="getParent"></slot>
      <template v-else>
        <span class="text-truncate justify-self-start" :class="{'waiting-for-functionality': waitingForFunctionality}">
          Formula: {{truncatedFormulaText}}
        </span>
      </template>
      <slot name="button" :onClick="editFormula">
        <suffix class="justify-self-end" :class="{'waiting-for-functionality': waitingForFunctionality}">
          <div class="d-flex ml-1">
            <formula-button @click.self="editFormula" :disabled="disabled"/>
          </div>
        </suffix>
      </slot>
    </column>
    <formula-modal
      v-if="editedFormula"
      :dynamic-options="getDynamicOptions"
      :formula="editedFormula"
      :state="modalStateFor(vueModalID.formulaEditor)"
      :index="vueModalID.formulaEditor"
      @toggle-modal="hideFormulaModal"
      :form="form"
      :object-models="allObjectModels"
      @saved="(eventData) => $emit('saved', eventData)"
      @add-widget="addWidget"
    ></formula-modal>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import FormulaModal from './formulaModal.vue';
  import FormulaMixin from "../../mixins/formulaMixin";
  import {IDynamicFieldsElementOptions} from "~/interfaces/dynamicFieldsElementOptionsInterfaces";
  import {IDynamicFieldsOptions} from "~/interfaces/dynamicFieldsInterfaces/dynamicOptionsInterfaces";
  import HelpText from '../helpText.vue';
  import ObjectModel from "~/models/objectModel";
  import { WidgetType } from '~/helpers/codeMirrorWidget'
  import { IDynamicDataConfig } from '~/interfaces/dynamicFieldsInterfaces'
  import FormulaButton from '~/components/formulas/formula-button.vue'

  export default Vue.extend({
    mixins: [
      FormulaMixin,
    ],
    components: {
      FormulaButton,
      FormulaModal,
      HelpText
    },
    props: {
      disabled: Boolean,
      fieldName: {
        type: String,
        required: true,
      },
      containerClass: {
        type: String,
        default: 'c-row',
      },
      dynamicFieldsHandler: {
        type: Object as () => IDynamicFieldsElementOptions,
      },
      dynamicOptions: {
        type: Array as () => Array<IDynamicFieldsOptions>,
        default: null,
      },
      truncateFormula: {
        type: Boolean,
        default: false
      },
      serversideWarningTooltip: {
        type: Boolean,
        default: false,
      },
      waitingForFunctionality: {
        type: Boolean,
        default: false,
      },
      objectModels: {
        type: Array as () => Array<ObjectModel>,
        default: () => []
      },
    },
    created() {
      this.generateEditedFormula();
    },
    mounted() {
      this.$emit('formula-initialized', {formulaObject: this.editedFormula});
    },
    methods: {
      editFormula(): void {
        /*
         *  the FormulaModal really wants a fresh formula when
         *  it's first opened for a currently-unknown reason :S
        */
        this.generateEditedFormula();
        this.showModal(this.vueModalID.formulaEditor);
      },
      hideFormulaModal(): void {
        this.hideModal(this.vueModalID.formulaEditor);
      },
      addWidget({type, config}: {type: WidgetType, config: IDynamicDataConfig}): void {
        this.$emit('add-widget', {type, config})
      },
    },
    computed: {
      form() {
        return this.getFieldsHandler?.form || this.getFieldsHandler?.handlerConfig?.find(x=> !!x.handler.form)?.handler?.form;
      },
      objectModel() {
        return this.getFieldsHandler?.objectModel || this.getFieldsHandler?.handlerConfig?.find(x=> !!x.handler.objectModel)?.handler?.objectModel;
      },
      allObjectModels() {
        const array = [...this.objectModels];
        if (this.objectModel) {
          array.push(this.objectModel);
        }
        return array;
      },
      getFieldsHandler() {
        if (this.dynamicFieldsHandler) {
          return this.dynamicFieldsHandler;
        } else {
          return this.dynamicOptions[0]?.props?.fieldsHandler;
        }
      },
      getDynamicOptions() {
        if (this.dynamicOptions) {
          return this.dynamicOptions;
        } else {
          return [
            {
              is: 'dynamic-fields-tree',
              props: {
                parent: this.parent,
                restrictResultsToParentContext: true,
                fieldsHandler: this.dynamicFieldsHandler,
              },
            },
          ];
        }
      },
      truncatedFormulaText(): string {
        if (this.truncateFormula && this.formulaText.length > 75) {
          return this.formulaText.substring(0, 75) + '...';
        }
        return this.formulaText;
      },
    }
  });
</script>
